import { BudgetAllowance } from '@frontend/budget/types';
import { Certification } from '@frontend/certificate/types';
import { EntityType } from '@frontend/common';
import { DefaultBadge, ObjectBadge } from '@frontend/components/badge';
import { Spinner } from '@frontend/components/common';
import { IGroup } from '@frontend/group/types';
import { CommonMessage, getCorrectTranslation } from '@frontend/lang';
import { IProduct, ProductType } from '@frontend/product/types';
import { Stock } from '@frontend/stock/types';
import { useEffect, useRef } from 'react';
import { LuCircleOff } from 'react-icons/lu';

import PlusMinusNumberInput from '../../../../components/plus-minus-number-input/plus-minus-number-input.component';
import { BudgetQuantity } from '../../../components/budget-quantity/budget-quantity.component';
import { useLeafCategoryView } from './leaf-category-view.controller';

export interface LeafCategoryViewProps {
    category: IGroup;
    onProductSelected?: (
        _product: IProduct | null,
        _stocks: Stock[],
        _amount: number,
        _allowance: BudgetAllowance | null,
        _certification: Certification | null,
        _canOrder: boolean
    ) => void;
}

export const LeafCategoryView = (props: LeafCategoryViewProps) => {
    const viewProps = useLeafCategoryView(props);
    const amountSectionRef = useRef<HTMLDivElement>(null);
    const wasOrderFallback = useRef(false);
    const showCertificationWarning =
        !viewProps.isRecycleMode &&
        !viewProps.isOrderFallback &&
        viewProps.matchedProduct?.type === ProductType.EQUIPMENT &&
        viewProps.certification?.certified !== true &&
        viewProps.stockInfo.hasStockInCurrentSpot;

    useEffect(() => {
        if (!viewProps.isOrderFallback) {
            wasOrderFallback.current = false;
            return;
        }

        if (wasOrderFallback.current || !amountSectionRef.current) {
            return;
        }

        const frame = window.requestAnimationFrame(() => {
            amountSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        wasOrderFallback.current = true;

        return () => window.cancelAnimationFrame(frame);
    }, [viewProps.currentProduct?.id, viewProps.isOrderFallback, viewProps.totalMax]);

    if (viewProps.isLoading) {
        return (
            <div className='flex h-full w-full items-center justify-center py-12'>
                <Spinner />
            </div>
        );
    }

    return (
        <div className='flex w-full flex-col items-start gap-6'>
            <div className='flex w-full flex-col items-start gap-3'>
                <div className='flex w-full items-start justify-between text-base font-medium'>
                    <div className='text-foreground-default flex flex-col justify-center'>
                        <p>{CommonMessage.OBJECTS.PRODUCT.DEFAULT}</p>
                    </div>
                </div>
                {props.category && (
                    <div className='bg-background-surface outline-border-default inline-flex items-start justify-start self-stretch rounded-xl p-3 outline-1 -outline-offset-1'>
                        <img
                            className='aspect-square h-full w-24 rounded-lg object-contain'
                            src={viewProps.image ?? viewProps.placeholder}
                            alt={props.category.name}
                        />

                        <div className='flex flex-1 flex-col items-start px-3 py-4'>
                            <div className='flex w-full flex-col items-start gap-1'>
                                <p className='text-foreground-default h-11.5 w-full text-base font-medium'>{props.category.name}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Attribute Options */}
            {viewProps.attributeOptions.map((attributeOption) => {
                const selectedCount = attributeOption.values.filter((val) => viewProps.isOptionAvailable(attributeOption.attribute.id, val.id)).length;

                return (
                    <div
                        key={attributeOption.attribute.id}
                        className='flex w-full flex-col items-start gap-3'>
                        <div className='text-foreground-default flex flex-col justify-center text-base font-medium'>
                            <p className='lowercase'>
                                {selectedCount} {getCorrectTranslation(viewProps.locale, attributeOption.attribute, 'name')}
                                {selectedCount !== 1 ? 's' : ''} {CommonMessage.OBJECTS.SLOT.STATUS.AVAILABLE}
                            </p>
                        </div>
                        <div className='flex w-full flex-wrap items-start gap-2'>
                            {attributeOption.values.map((value) => {
                                const isSelected = viewProps.selectedAttributes[attributeOption.attribute.id] === value.id;
                                const isAvailable = viewProps.isOptionAvailable(attributeOption.attribute.id, value.id);

                                return (
                                    <button
                                        key={value.id}
                                        onClick={() => isAvailable && viewProps.selectAttribute(attributeOption.attribute.id, value.id)}
                                        disabled={!isAvailable}
                                        className={`flex h-11 min-w-11 flex-col items-center justify-center rounded-xl px-3 py-3 text-center text-base font-medium capitalize ${
                                            isAvailable
                                                ? isSelected
                                                    ? 'bg-brand-primary text-always-white outline-border-default outline-1 -outline-offset-1'
                                                    : 'bg-background-surface text-foreground-default outline-border-default outline-1 -outline-offset-1'
                                                : 'bg-brand-disabled text-background-default cursor-not-allowed'
                                        } `}>
                                        {value.value}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {/* Message when product matched but only available in other spots */}
            {!viewProps.isRecycleMode &&
                !viewProps.isOrderFallback &&
                viewProps.matchedProduct &&
                !viewProps.stockInfo.hasStockInCurrentSpot &&
                viewProps.stockInfo.hasStockInOtherSpots && (
                    <div className='bg-background-surface outline-border-default flex w-full flex-col items-center gap-2 rounded-xl border p-4 outline -outline-offset-1'>
                        <div className='flex w-full items-center gap-3'>
                            <LuCircleOff className='text-foreground-default h-5 w-5' />
                            <div className='text-foreground-default text-base font-medium'>
                                {CommonMessage.ERRORS.NOT_IN_STOCK}. {CommonMessage.ERRORS.PRODUCT_AVAILABLE_IN_FOLLOWING_LOCATIONS}
                            </div>
                        </div>
                        <div className='flex w-full flex-wrap justify-start gap-2'>
                            {viewProps.stockInfo.otherSpotIds.map((spotId) => (
                                <ObjectBadge
                                    key={spotId}
                                    id={spotId}
                                    entityType={EntityType.SPOT}
                                    disableClick
                                    styles={{ icon: 'font-semibold size-4' }}
                                    className='outline-border-default flex h-6 items-center gap-1 font-semibold outline-1'
                                />
                            ))}
                        </div>
                    </div>
                )}

            {showCertificationWarning && (
                <div className='bg-background-surface outline-border-default flex w-full items-center gap-3 rounded-xl border p-4 outline -outline-offset-1'>
                    <LuCircleOff className='text-foreground-default h-5 w-5' />
                    <div className='text-foreground-default text-base font-medium'>{CommonMessage.ERRORS.NOT_CERTIFIED}</div>
                </div>
            )}

            {/* Quantity Selector - Only show when a product is selected */}
            {!viewProps.isRecycleMode && viewProps.currentProduct && viewProps.totalMax !== null && (
                <div
                    ref={amountSectionRef}
                    className='flex w-full scroll-mb-24 flex-col items-start gap-3'>
                    <div className='text-foreground-default flex flex-col justify-center text-base font-medium'>
                        <p>{CommonMessage.OBJECTS.COMMON_FIELDS.AMOUNT}</p>
                    </div>
                    <PlusMinusNumberInput
                        min={1}
                        max={viewProps.totalMax}
                        value={viewProps.amount}
                        onChange={viewProps.setAmount}
                    />
                    {/* Show availability info */}
                    <div className='text-foreground-default flex w-full justify-start gap-1 text-sm font-medium'>
                        {viewProps.maxBudget && Object.keys(viewProps.maxBudget.budgets).length > 0 && (
                            <DefaultBadge
                                styles={{ container: { color: 'bg-brand-muted' } }}
                                label={
                                    <div className='flex gap-1'>
                                        <p>{CommonMessage.OBJECTS.BUDGET.DEFAULT}:</p>
                                        <p>
                                            <BudgetQuantity quantity={viewProps.maxBudget.quantity} />
                                        </p>
                                    </div>
                                }
                            />
                        )}

                        {!viewProps.isOrderFallback && (
                            <DefaultBadge
                                styles={{ container: { color: 'bg-brand-muted' } }}
                                label={
                                    <div className='flex gap-1'>
                                        <p>{CommonMessage.OBJECTS.STOCK.DEFAULT}:</p>
                                        <p>{viewProps.maxStock?.quantity ?? 0}</p>
                                    </div>
                                }
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
